import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from './Navigation';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { 
  Target,
  BarChart3,
  Brain,
  Shield,
  TrendingUp,
  CheckCircle,
  Zap,
  Users,
  Building2,
  ArrowRight,
  Calendar,
  Mail,
  Award,
  Globe,
  Lightbulb,
  Calculator,
} from 'lucide-react';

const AboutPage = () => {
  const smartFramework = [
    {
      letter: 'S',
      title: 'Solution-Focused',
      description: 'We address your specific business challenges',
      icon: Target,
      color: 'from-blue-500 to-blue-600'
    },
    {
      letter: 'M',
      title: 'Measurable',
      description: 'Clear, data-driven results you can track',
      icon: BarChart3,
      color: 'from-teal-500 to-teal-600'
    },
    {
      letter: 'A',
      title: 'AI-Driven',
      description: 'Advanced technology that enhances decision-making',
      icon: Brain,
      color: 'from-purple-500 to-purple-600'
    },
    {
      letter: 'R',
      title: 'Reliable',
      description: 'Consistent, uninterrupted performance',
      icon: Shield,
      color: 'from-green-500 to-green-600'
    },
    {
      letter: 'T',
      title: 'Transformative',
      description: 'Long-term change that drives growth',
      icon: TrendingUp,
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const principles = [
    {
      title: 'Ethics',
      description: 'Transparent, fair, and secure AI that protects privacy',
      icon: CheckCircle,
      gradient: 'from-[#112d56] to-[#1a3d6b]'
    },
    {
      title: 'Efficiency',
      description: 'Streamlined workflows that save time and resources',
      icon: Zap,
      gradient: 'from-[#ff7a1f] to-[#e6681b]'
    },
    {
      title: 'Effectiveness',
      description: 'Measurable outcomes that deliver real business impact',
      icon: Target,
      gradient: 'from-teal-600 to-teal-700'
    }
  ];

  const stats = [
    { number: '20+', label: 'Years Combined Experience' },
    { number: '100%', label: 'Focus on Voice AI' },
    { number: 'EU', label: 'AI Act Compliant' },
    { number: '24/7', label: 'Reliable Performance' }
  ];

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#112d56] via-[#1a3d6b] to-[#112d56] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent"></div>
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-[#ff7a1f]/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-white/5 rounded-full blur-xl"></div>
        </div>
        
        <div className="relative container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h1 className="text-5xl lg:text-7xl font-bold leading-tight tracking-tight">
              About <span className="text-[#ff7a1f]">Smart AI Solutions</span>
            </h1>
            <p className="text-xl lg:text-2xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              The AI Innovation Company Behind VioQbase
            </p>
            <p className="text-lg text-blue-200 max-w-2xl mx-auto">
              Empowering businesses with intelligent voice AI solutions that streamline operations and maximize productivity.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
              <Button
                asChild
                size="lg"
                className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-8 py-4 text-lg rounded-xl transition-all duration-300 transform hover:scale-105"
              >
                <Link to="/features">
                  <Lightbulb className="mr-2 h-5 w-5" />
                  Learn About VioQbase
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-[#112d56] font-bold px-8 py-4 text-lg rounded-xl transition-all duration-300"
              >
                <Link to="/contact">
                  <Mail className="mr-2 h-5 w-5" />
                  Contact Us
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Company Introduction Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-5 gap-12 items-center">
              {/* Left Column - Content */}
              <div className="lg:col-span-3 space-y-8">
                <div>
                  <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                    About Us
                  </h2>
                  <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                    <p>
                      <strong className="text-[#112d56]">Smart AI Solutions</strong> is the company behind VioQbase, founded by a team of professionals with over <strong className="text-[#ff7a1f]">20 years of combined experience</strong> in HR and employee training.
                    </p>
                    <p>
                      We specialize in cutting-edge voice AI technologies that help businesses streamline operations, boost productivity, and maximize ROI. Our flagship solution, <strong className="text-[#ff7a1f]">VioQbase</strong>, empowers companies to automate employee support through intelligent voice interactions.
                    </p>
                  </div>
                </div>
                
                {/* Key Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {stats.map((stat, index) => (
                    <Card key={index} className="border-0 shadow-md text-center bg-white">
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-[#ff7a1f] mb-1">{stat.number}</div>
                        <div className="text-sm text-gray-600">{stat.label}</div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
              
              {/* Right Column - Visual */}
              <div className="lg:col-span-2">
                <Card className="border-0 shadow-2xl overflow-hidden">
                  <CardContent className="p-0">
                    <div className="bg-gradient-to-br from-[#112d56] to-[#1a3d6b] p-12 text-white text-center">
                      <Building2 className="h-20 w-20 text-[#ff7a1f] mx-auto mb-6" />
                      <h3 className="text-2xl font-bold mb-4">Innovation Hub</h3>
                      <p className="text-blue-200 mb-6">Where AI meets workplace efficiency</p>
                      <div className="space-y-3 text-sm">
                        <div className="flex items-center justify-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-[#ff7a1f]" />
                          <span>HR Technology Experts</span>
                        </div>
                        <div className="flex items-center justify-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-[#ff7a1f]" />
                          <span>AI Innovation Leaders</span>
                        </div>
                        <div className="flex items-center justify-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-[#ff7a1f]" />
                          <span>Enterprise Solutions</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Card className="border-0 shadow-2xl bg-gradient-to-r from-[#112d56]/5 to-[#ff7a1f]/5 overflow-hidden">
              <CardContent className="p-12">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-[#ff7a1f] rounded-full flex items-center justify-center">
                    <Target className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h2 className="text-3xl lg:text-4xl font-bold text-[#112d56] mb-6">
                  Our Mission
                </h2>
                <p className="text-xl text-gray-700 leading-relaxed max-w-2xl mx-auto">
                  To make workplace support more efficient and accessible through responsible AI innovation.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* SMART Framework Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Our Approach: The <span className="text-[#ff7a1f]">SMART</span> Framework
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Every solution we build follows our proven methodology:
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {smartFramework.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden">
                    <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                    <CardContent className="p-8 text-center relative">
                      <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                        <span className="text-2xl font-bold text-white">{item.letter}</span>
                      </div>
                      <IconComponent className="h-8 w-8 text-[#ff7a1f] mx-auto mb-4" />
                      <h3 className="text-lg font-bold text-[#112d56] mb-3">{item.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 3E Principles Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-[#112d56] mb-6">
                Our Values: The <span className="text-[#ff7a1f]">3E</span> Principles
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {principles.map((principle, index) => {
                const IconComponent = principle.icon;
                return (
                  <Card key={index} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 group overflow-hidden">
                    <div className={`h-2 bg-gradient-to-r ${principle.gradient}`}></div>
                    <CardContent className="p-8 text-center">
                      <div className={`w-16 h-16 bg-gradient-to-br ${principle.gradient} rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className="h-8 w-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-[#112d56] mb-4">✓ {principle.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{principle.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#112d56] to-[#1a3d6b] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="space-y-8">
              <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                Ready to Transform Your <span className="text-[#ff7a1f]">Workplace Efficiency</span>?
              </h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto">
                Are you ready to become one of our technology partners who trust Smart AI Solutions to revolutionize their employee support with VioQbase?
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
                <Button
                  asChild
                  size="lg"
                  className="bg-[#ff7a1f] hover:bg-[#e6681b] text-white font-bold px-8 py-4 text-lg rounded-xl transition-all duration-300 transform hover:scale-105"
                >
                  <Link 
                    to="/#assessment"
                    onClick={(e) => {
                      // If we're already on the home page, scroll to assessment
                      if (window.location.pathname === '/') {
                        e.preventDefault();
                        const element = document.getElementById('assessment');
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                        }
                      }
                    }}
                  >
                    <Calculator className="mr-2 h-5 w-5" />
                    Try VioQbase Value Check Assessment
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-[#112d56] font-bold px-8 py-4 text-lg rounded-xl transition-all duration-300"
                >
                  <Link to="/contact">
                    <Calendar className="mr-2 h-5 w-5" />
                    Contact Us
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Added VioQbase footer */}
      <footer className="bg-[#112d56] text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center space-x-2 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vioq-content-update/artifacts/w40y3694_Smart%20AI%20Logo%20white.png"
                  alt="Smart AI Solutions"
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-blue-200 leading-relaxed mb-8 max-w-xs">
                Voice-powered AI knowledge base for enterprise teams. Get instant answers, eliminate delays.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Product</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/features" className="hover:text-[#ff7a1f] transition-colors text-lg">Features</a></li>
                <li><a href="/use-cases" className="hover:text-[#ff7a1f] transition-colors text-lg">Use Cases</a></li>
                <li><a href="/products/concierge" className="hover:text-[#ff7a1f] transition-colors text-lg">Smart Parrot Concierge</a></li>
                <li><a href="/products/viocoach" className="hover:text-[#ff7a1f] transition-colors text-lg">VioCoach</a></li>
                <li><a href="https://cal.com/smartaisolutions/vioqbase-demo" target="_blank" rel="noopener noreferrer" className="hover:text-[#ff7a1f] transition-colors text-lg">Book Demo</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Company</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/about" className="hover:text-[#ff7a1f] transition-colors text-lg">About</a></li>
                <li><a href="/contact" className="hover:text-[#ff7a1f] transition-colors text-lg">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-xl mb-6 text-white">Legal</h4>
              <ul className="space-y-3 text-blue-200">
                <li><a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a></li>
                <li><a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a></li>
              </ul>
            </div>
          </div>
          
          <Separator className="bg-blue-800/50 my-8" />
          
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-blue-200 text-lg">
              © 2025 VioQbase. All rights reserved.
            </p>
            <div className="flex space-x-8 text-blue-200 mt-4 md:mt-0">
              <a href="/privacy" className="hover:text-[#ff7a1f] transition-colors text-lg">Privacy Policy</a>
              <a href="/terms" className="hover:text-[#ff7a1f] transition-colors text-lg">Terms of Service</a>
              <a href="/cookies" className="hover:text-[#ff7a1f] transition-colors text-lg">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;